import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Lock, Users, MessageSquare, Video, Clock, ChevronLeft,
  Sparkles, Check, X, Send, Crown, UserMinus, Shield, LayoutDashboard,
  Link2, Copy, Trash2, Plus, ImagePlus,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import ChatMessageBody from "@/components/ChatMessageBody";
import UserBadge from "@/components/UserBadge";
import CommunityRecentJoins from "@/components/community/CommunityRecentJoins";
import CommunityDmInviteModal from "@/components/community/CommunityDmInviteModal";
import { isNativeIOS } from "@/lib/nativePush";

/**
 * CommunityDetail — the /c/:id page. Renders 3 flavours depending on the
 * viewer's access_level:
 *   • gated / public / requested → teaser with Request-to-Join CTA
 *   • member                     → tabbed experience (Chat / Members / Cam)
 *   • owner / moderator          → same + a management drawer
 *
 * Detail data is fetched once on mount; tab data is lazy-loaded when the
 * tab is opened. Manager sees a pending-requests badge on the Members
 * tab if requests > 0.
 */
export default function CommunityDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authChecking = user === null;

  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("chat");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/communities/${id}`);
      setCommunity(data);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("Community not found.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (!authChecking) load(); }, [authChecking, load]);

  // Invite-link auto-redeem. If the URL has `?invite=<code>`, hit the
  // redeem endpoint once the auth state has resolved. On success we
  // reload the community, drop the invite param so a refresh doesn't
  // re-run the redeem, and let the user land straight in the room.
  useEffect(() => {
    const code = searchParams.get("invite");
    if (!code || authChecking) return;
    if (!user) {
      // Preserve the invite through login by encoding it into `next=`.
      const backTo = `/c/${id}?invite=${encodeURIComponent(code)}`;
      navigate(`/login?next=${encodeURIComponent(backTo)}`);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/communities/${id}/invites/${code}/redeem`);
        if (cancelled) return;
        if (data.already) {
          toast.success("You're already a member — welcome back.");
        } else {
          toast.success("Invite redeemed. You're in.");
        }
        // Strip the query param without a re-navigation so state stays clean.
        setSearchParams({}, { replace: true });
        load();
      } catch (e) {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Invite could not be redeemed.");
        setSearchParams({}, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authChecking, user, id]);

  if (loading || authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading community…
      </div>
    );
  }
  if (!community) return null;

  const requestJoin = async () => {
    if (!user) return navigate(`/login?next=${encodeURIComponent(`/c/${id}`)}`);
    setBusy(true);
    try {
      await api.post(`/communities/${id}/join-requests`, { note: null });
      toast.success("Request sent — the owner will review.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request.");
    } finally {
      setBusy(false);
    }
  };

  const joinOpen = async () => {
    setBusy(true);
    try {
      await api.post(`/communities/${id}/join`);
      toast.success(`Joined ${community.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join.");
    } finally {
      setBusy(false);
    }
  };

  const access = community.access_level;
  const isMember = ["owner", "moderator", "member"].includes(access);
  const isManager = ["owner", "moderator"].includes(access);
  const isPrivate = community.visibility === "private";

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="community-detail-page">
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <Link
          to="/communities"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"
          data-testid="community-detail-back"
        >
          <ChevronLeft size={14} /> Communities
        </Link>

        {/* Header */}
        <div className="border-b border-white/10 pb-6">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-lg overflow-hidden bg-gradient-to-br from-rose-900/30 to-black shrink-0 flex items-center justify-center">
              {community.cover_url ? (
                <img src={community.cover_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Users size={30} className="text-rose-500/40" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl">{community.name}</h1>
                {isPrivate && (
                  <span
                    data-testid="community-detail-private-badge"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-full px-2 py-0.5"
                  >
                    <Lock size={10} /> Private
                  </span>
                )}
                {access === "owner" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-500/10 border border-amber-400/30 text-amber-300 rounded-full px-2 py-0.5">
                    <Crown size={10} /> Owner
                  </span>
                )}
                {access === "moderator" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 rounded-full px-2 py-0.5">
                    <Shield size={10} /> Moderator
                  </span>
                )}
              </div>
              <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2">
                <Users size={12} /> {(community.members_count || 0).toLocaleString()} members
              </div>
              {(isMember || !isPrivate) && community.description && (
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                  {community.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Access-level branches */}
        {!isMember ? (
          <GatedView
            community={community}
            access={access}
            busy={busy}
            onRequest={requestJoin}
            onOpenJoin={joinOpen}
            isLoggedIn={!!user}
          />
        ) : (
          <>
            {/* Recent joins ticker — shown just above the tabs so members
                and owners see growth at a glance without switching tab. */}
            <CommunityRecentJoins communityId={id} />

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-white/10 mt-6" data-testid="community-tabs">
              <TabButton active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare} label="Chat" testid="community-tab-chat" />
              <TabButton active={tab === "members"} onClick={() => setTab("members")} icon={Users} label="Members" testid="community-tab-members" />
              <TabButton
                active={tab === "cam"}
                onClick={() => setTab("cam")}
                icon={Video}
                label="Cam Room"
                testid="community-tab-cam"
                badge={<CamStatusPill communityId={id} />}
              />
              {isManager && (
                <TabButton active={tab === "manage"} onClick={() => setTab("manage")} icon={LayoutDashboard} label="Manage" testid="community-tab-manage" />
              )}
            </div>
            <div className="mt-4">
              {tab === "chat" && <ChatPanel communityId={id} />}
              {tab === "members" && <MembersPanel communityId={id} isManager={isManager} isOwner={access === "owner"} onChange={load} />}
              {tab === "cam" && <CamPanel communityId={id} />}
              {tab === "manage" && isManager && <ManagePanel communityId={id} communityName={community.name} onChange={load} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors ${
        active
          ? "border-rose-500 text-white"
          : "border-transparent text-zinc-500 hover:text-white"
      }`}
    >
      <Icon size={14} /> {label}
      {badge}
    </button>
  );
}

/** Live-cam participant pill on the "Cam Room" tab. Polls every 15s. */
function CamStatusPill({ communityId }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/communities/${communityId}/private-cam/status`);
        if (!cancelled) setCount(data?.count || 0);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, 15_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [communityId]);

  if (count <= 0) return null;
  return (
    <span
      data-testid="community-cam-live-pill"
      className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 rounded-full px-1.5 py-0.5"
      title={`${count} in the Cam Room`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      {count}
    </span>
  );
}

function GatedView({ community, access, busy, onRequest, onOpenJoin, isLoggedIn }) {
  if (access === "requested") {
    return (
      <div className="mt-8 text-center py-12" data-testid="community-detail-requested">
        <Clock size={32} className="mx-auto text-amber-400 mb-3" />
        <h2 className="font-display text-2xl mb-2">Request in review</h2>
        <p className="text-sm text-zinc-400 max-w-sm mx-auto leading-relaxed">
          The owner will get a notification and decide when they can. You'll be pinged the
          moment they respond.
        </p>
      </div>
    );
  }
  const needsRequest = community.visibility === "private" || community.join_policy === "request";
  return (
    <div className="mt-8 text-center py-12" data-testid="community-detail-gated">
      {community.visibility === "private" ? (
        <>
          <Lock size={32} className="mx-auto text-rose-400 mb-3" />
          <h2 className="font-display text-2xl mb-2">Members only</h2>
          <p className="text-sm text-zinc-400 max-w-sm mx-auto leading-relaxed">
            This community is invite-only. Request to join and we'll let the owner know.
          </p>
        </>
      ) : (
        <>
          <Sparkles size={32} className="mx-auto text-rose-400 mb-3" />
          <h2 className="font-display text-2xl mb-2">Join {community.name}</h2>
          <p className="text-sm text-zinc-400 max-w-sm mx-auto leading-relaxed">
            {needsRequest
              ? "This community requires approval to join."
              : "One tap and you're in."}
          </p>
        </>
      )}
      <button
        type="button"
        onClick={needsRequest ? onRequest : onOpenJoin}
        disabled={busy}
        data-testid="community-detail-primary-cta"
        className="mt-6 inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3 text-sm"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {!isLoggedIn ? "Sign in to join" : needsRequest ? "Request to Join" : "Join community"}
      </button>
    </div>
  );
}

// ─── Chat ────────────────────────────────────────────────────────────────
//
// Approved members can:
//   • Type a text message (Enter to send, Shift+Enter for newline is TODO — the
//     input is single-line for now)
//   • Attach an image via the paperclip button
//   • Drag-and-drop an image onto the chat pane (full-pane overlay lights up)
//   • Paste an image straight from clipboard (⌘V / Ctrl+V)
//
// The upload goes through /api/uploads → Emergent Object Storage, returning a
// stable `/api/files/{id}` URL that we save on the message row. The chat pane
// renders `image_url` as an inline thumbnail with a click-to-enlarge overlay.
function ChatPanel({ communityId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { url, uploading, previewUrl }
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/communities/${communityId}/messages?limit=50`);
      setMessages(data.messages || []);
    } catch { /* silent */ }
  }, [communityId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10_000); // poll for new msgs while chat is open
    return () => clearInterval(iv);
  }, [load]);

  // Auto-scroll to newest message whenever the list grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Upload one File → { url } from the backend. Also handles a local preview
  // via URL.createObjectURL so senders see the image immediately even before
  // the upload finishes.
  const uploadFile = async (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      toast.error("Only image files are supported.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Image must be under 25 MB.");
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingImage({ url: null, uploading: true, previewUrl });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // `data.url` is fully qualified (/api/files/{id} on our origin).
      setPendingImage({ url: data.url, uploading: false, previewUrl });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
      URL.revokeObjectURL(previewUrl);
      setPendingImage(null);
    }
  };

  const send = async () => {
    const trimmed = text.trim();
    const image = pendingImage?.url;
    if ((!trimmed && !image) || sending || pendingImage?.uploading) return;
    setSending(true);
    try {
      const body = { text: trimmed };
      if (image) body.image_url = image;
      const { data } = await api.post(`/communities/${communityId}/messages`, body);
      setMessages((prev) => [...prev, data]);
      setText("");
      if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
      setPendingImage(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send.");
    } finally {
      setSending(false);
    }
  };

  // Drag-and-drop overlay: uses a ref counter because dragenter/dragleave fire
  // for every child element and we only want to flip state on the pane
  // boundary.
  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragOver(true);
  };
  const onDragLeave = () => {
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragOver(false);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  // Clipboard paste — matches DM composer parity.
  const onPaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.type?.startsWith("image/"));
    if (item) {
      const file = item.getAsFile();
      if (file) uploadFile(file);
    }
  };

  return (
    <div
      className="border border-white/10 rounded-md overflow-hidden relative"
      data-testid="community-chat-panel"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dragOver && (
        <div
          className="absolute inset-0 z-20 bg-rose-950/80 backdrop-blur-sm border-2 border-dashed border-rose-500 flex items-center justify-center pointer-events-none"
          data-testid="community-chat-drop-overlay"
        >
          <div className="text-center">
            <ImagePlus size={48} className="mx-auto text-rose-400 mb-3" />
            <div className="text-lg font-display text-rose-100">Drop image to share</div>
            <div className="text-xs text-rose-300/70 mt-1 uppercase tracking-widest">JPEG · PNG · WEBP · GIF · max 25 MB</div>
          </div>
        </div>
      )}
      <div ref={scrollRef} className="h-[420px] overflow-y-auto p-4 space-y-3 bg-black/40">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-zinc-500 py-12">
            No messages yet. Say hi 👋 Drop an image, or paste one.
          </div>
        ) : messages.map((m) => (
          <div key={m.id} className="flex items-start gap-2" data-testid={`community-message-${m.id}`}>
            <UserBadge
              userRef={m.author?.id || m.author?.handle}
              href={m.author?.handle ? `/u/${m.author.id}` : undefined}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-full shrink-0"
              data-testid={`community-chat-avatar-${m.id}`}
            >
              <Avatar className="w-8 h-8 border border-white/10">
                <AvatarImage src={m.author?.avatar_url} />
                <AvatarFallback className="bg-rose-600 text-white text-[10px]">
                  {m.author?.display_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </UserBadge>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-400 flex items-center gap-2">
                <UserBadge
                  userRef={m.author?.id || m.author?.handle}
                  href={m.author?.handle ? `/u/${m.author.id}` : undefined}
                  as="span"
                  className="text-white cursor-pointer hover:text-rose-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-sm"
                >
                  {m.author?.display_name || m.author?.handle}
                </UserBadge>
                <span className="text-zinc-600">·</span>
                <span>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              {m.image_url && (
                <button
                  type="button"
                  onClick={() => setLightbox(m.image_url)}
                  className="block mt-1.5 group"
                  data-testid={`community-message-image-${m.id}`}
                >
                  <img
                    src={m.image_url}
                    alt=""
                    className="max-h-56 max-w-[280px] rounded-md border border-white/10 object-cover group-hover:brightness-110 transition"
                    loading="lazy"
                  />
                </button>
              )}
              {m.text && (
                <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed mt-1">
                  <ChatMessageBody text={m.text} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pending-image tray — shows above the input while an upload is in
          flight or ready-to-send. Cancel button clears it. */}
      {pendingImage && (
        <div
          className="border-t border-white/10 p-2 bg-black/60 flex items-center gap-2"
          data-testid="community-chat-pending-image"
        >
          <div className="relative">
            <img
              src={pendingImage.previewUrl}
              alt=""
              className={`h-14 w-14 object-cover rounded border border-white/15 ${pendingImage.uploading ? "opacity-40" : ""}`}
            />
            {pendingImage.uploading && (
              <Loader2 size={16} className="absolute inset-0 m-auto animate-spin text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-zinc-300">
              {pendingImage.uploading ? "Uploading…" : "Ready to send"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Attach an image</div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (pendingImage.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
              setPendingImage(null);
            }}
            data-testid="community-chat-remove-image"
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-rose-600 text-white flex items-center justify-center"
            aria-label="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="border-t border-white/10 p-2 flex items-center gap-2 bg-zinc-950">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          data-testid="community-chat-attach-button"
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 flex items-center justify-center flex-shrink-0"
          aria-label="Attach image"
        >
          <ImagePlus size={16} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadFile(f);
            e.target.value = "";
          }}
          data-testid="community-chat-file-input"
        />
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 2000))}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          onPaste={onPaste}
          data-testid="community-chat-input"
          placeholder={pendingImage ? "Add a caption (optional)…" : "Message the community…"}
          className="flex-1 bg-transparent text-sm outline-none px-2 py-2"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || pendingImage?.uploading || (!text.trim() && !pendingImage?.url)}
          data-testid="community-chat-send"
          className="w-9 h-9 rounded-full bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white flex items-center justify-center"
          aria-label="Send"
        >
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>

      {/* Click-to-enlarge lightbox for any chat image. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="community-chat-lightbox"
        >
          <img
            src={lightbox}
            alt=""
            className="max-h-full max-w-full object-contain shadow-2xl border border-white/10"
          />
          <button
            type="button"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Members ─────────────────────────────────────────────────────────────
function MembersPanel({ communityId, isManager, isOwner, onChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/communities/${communityId}/members`);
      setMembers(data.members || []);
    } catch {}
    finally { setLoading(false); }
  }, [communityId]);

  useEffect(() => { load(); }, [load]);

  const kick = async (uid, handle) => {
    if (!window.confirm(`Remove @${handle} from the community?`)) return;
    try {
      await api.post(`/communities/${communityId}/members/${uid}/kick`);
      toast.success(`@${handle} removed.`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not kick.");
    }
  };

  const setRole = async (uid, handle, role) => {
    try {
      await api.post(`/communities/${communityId}/members/${uid}/role`, { role });
      toast.success(`@${handle} → ${role}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role.");
    }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-8 text-center"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading members…</div>;

  return (
    <ul className="border border-white/10 rounded-md divide-y divide-white/5 overflow-hidden">
      {members.map((m) => (
        <li key={m.id} className="flex items-center gap-3 px-4 py-3 bg-zinc-950/40" data-testid={`community-member-${m.user?.id}`}>
          <UserBadge
            userRef={m.user?.id || m.user?.handle}
            href={m.user?.handle ? `/u/${m.user.id}` : undefined}
            className="flex items-center gap-3 min-w-0 flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-md"
          >
            <Avatar className="w-9 h-9 border border-white/10">
              <AvatarImage src={m.user?.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white text-xs">
                {m.user?.display_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm text-white truncate flex items-center gap-1.5">
                {m.user?.display_name}
                {m.role === "owner" && <Crown size={11} className="text-amber-400" />}
                {m.role === "moderator" && <Shield size={11} className="text-emerald-400" />}
              </div>
              <div className="text-[11px] text-zinc-500">{m.user?.display_name} · joined {new Date(m.created_at).toLocaleDateString()}</div>
            </div>
          </UserBadge>
          {isManager && m.role !== "owner" && (
            <div className="flex items-center gap-2 shrink-0">
              {isOwner && m.role !== "moderator" && (
                <button
                  type="button"
                  onClick={() => setRole(m.user.id, m.user.handle, "moderator")}
                  data-testid={`community-promote-${m.user?.id}`}
                  className="text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 rounded-full px-2 py-1"
                >
                  Make Mod
                </button>
              )}
              {isOwner && m.role === "moderator" && (
                <button
                  type="button"
                  onClick={() => setRole(m.user.id, m.user.handle, "member")}
                  data-testid={`community-demote-${m.user?.id}`}
                  className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 rounded-full px-2 py-1"
                >
                  Demote
                </button>
              )}
              <button
                type="button"
                onClick={() => kick(m.user.id, m.user.handle)}
                data-testid={`community-kick-${m.user?.id}`}
                className="text-rose-300 hover:text-rose-200 border border-rose-500/40 rounded-full w-7 h-7 flex items-center justify-center"
                aria-label="Remove"
                title="Remove"
              >
                <UserMinus size={12} />
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ─── Manage (join-requests inbox for owners/mods) ────────────────────────
function ManagePanel({ communityId, communityName, onChange }) {
  return (
    <div className="space-y-8" data-testid="community-manage-panel">
      <div>
        <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-3">
          Pending join requests
        </div>
        <JoinRequestsList communityId={communityId} onChange={onChange} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-3">
          Invite links
        </div>
        <InviteLinks communityId={communityId} communityName={communityName} />
      </div>
    </div>
  );
}

function JoinRequestsList({ communityId, onChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/communities/${communityId}/join-requests`);
      setRequests(data.requests || []);
    } catch {}
    finally { setLoading(false); }
  }, [communityId]);

  useEffect(() => { load(); }, [load]);

  const respond = async (r, approve) => {
    setBusyId(r.id);
    try {
      await api.post(`/communities/join-requests/${r.id}/respond`, { approve });
      toast.success(approve ? `Approved ${r.user?.display_name}` : `Denied ${r.user?.display_name}`);
      setRequests((prev) => prev.filter((x) => x.id !== r.id));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-8 text-center"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading requests…</div>;

  if (requests.length === 0) {
    return (
      <div className="text-center text-sm text-zinc-500 py-12" data-testid="community-manage-empty">
        No pending requests. Sit back — you'll see them here as they roll in.
      </div>
    );
  }

  return (
    <ul className="border border-white/10 rounded-md divide-y divide-white/5 overflow-hidden">
      {requests.map((r) => (
        <li key={r.id} className="px-4 py-3 bg-zinc-950/40" data-testid={`community-join-req-${r.user?.id}`}>
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 border border-white/10">
              <AvatarImage src={r.user?.avatar_url} />
              <AvatarFallback className="bg-rose-600 text-white text-xs">
                {r.user?.display_name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{r.user?.display_name}</div>
              <div className="text-[11px] text-zinc-500">{r.user?.display_name} · asked {new Date(r.created_at).toLocaleDateString()}</div>
            </div>
            <button
              type="button"
              onClick={() => respond(r, false)}
              disabled={busyId === r.id}
              data-testid={`community-deny-${r.user?.id}`}
              className="w-8 h-8 rounded-full bg-transparent border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-60 text-rose-300 flex items-center justify-center"
              aria-label="Deny"
            >
              <X size={14} />
            </button>
            <button
              type="button"
              onClick={() => respond(r, true)}
              disabled={busyId === r.id}
              data-testid={`community-approve-${r.user?.id}`}
              className="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black flex items-center justify-center"
              aria-label="Approve"
            >
              {busyId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            </button>
          </div>
          {r.note && (
            <p className="text-xs text-zinc-400 mt-2 italic border-l-2 border-rose-500/40 pl-3">
              "{r.note}"
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

// ─── Invite Links (owner/mod only) ───────────────────────────────────────
function InviteLinks({ communityId, communityName }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [maxUses, setMaxUses] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("7");
  const [dmCode, setDmCode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/communities/${communityId}/invites`);
      setInvites(data.invites || []);
    } catch {}
    finally { setLoading(false); }
  }, [communityId]);

  useEffect(() => { load(); }, [load]);

  const buildUrl = (code) =>
    typeof window !== "undefined"
      ? `${window.location.origin}/c/${communityId}?invite=${code}`
      : `/c/${communityId}?invite=${code}`;

  const create = async () => {
    setCreating(true);
    try {
      await api.post(`/communities/${communityId}/invites`, {
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        expires_in_days: expiresInDays ? parseInt(expiresInDays, 10) : null,
      });
      toast.success("Invite link created.");
      setMaxUses("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(buildUrl(code));
      toast.success("Invite link copied.");
    } catch {
      toast.error("Copy failed — select manually.");
    }
  };

  const revoke = async (code) => {
    if (!window.confirm("Revoke this invite? Existing users who redeemed it stay.")) return;
    try {
      await api.delete(`/communities/${communityId}/invites/${code}`);
      toast.success("Invite revoked.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed.");
    }
  };

  return (
    <div className="border border-white/10 rounded-md p-4 bg-zinc-950/40" data-testid="community-invites-section">
      <p className="text-xs text-zinc-400 leading-relaxed mb-4">
        Share these links with people you want in — clicking one skips the request queue and
        adds them as a member instantly.
      </p>

      {/* Create form */}
      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
            Max uses
          </label>
          <input
            type="number"
            min="1"
            inputMode="numeric"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
            data-testid="community-invite-max-uses"
            placeholder="Unlimited"
            className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs outline-none focus:border-rose-500"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
            Expires in (days)
          </label>
          <input
            type="number"
            min="1"
            inputMode="numeric"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
            data-testid="community-invite-expires"
            placeholder="Never"
            className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs outline-none focus:border-rose-500"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={create}
            disabled={creating}
            data-testid="community-invite-create-button"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-semibold rounded-full px-3 py-1.5 text-xs"
          >
            {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            New invite link
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 py-4 text-center">
          <Loader2 size={12} className="inline animate-spin mr-1" /> Loading…
        </div>
      ) : invites.length === 0 ? (
        <div className="text-center text-xs text-zinc-500 py-6" data-testid="community-invites-empty">
          No invite links yet. Create one to skip the request queue.
        </div>
      ) : (
        <ul className="divide-y divide-white/5 border-t border-white/5">
          {invites.map((inv) => (
            <li
              key={inv.code}
              className="flex items-center gap-3 py-2"
              data-testid={`community-invite-row-${inv.code}`}
            >
              <Link2 size={13} className={inv.is_active ? "text-emerald-400" : "text-zinc-600"} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-300 font-mono truncate">{buildUrl(inv.code)}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {inv.uses_count}/{inv.max_uses ?? "∞"} uses
                  {inv.expires_at && (
                    <> · expires {new Date(inv.expires_at).toLocaleDateString()}</>
                  )}
                  {!inv.is_active && <span className="text-rose-300"> · inactive</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => copy(inv.code)}
                data-testid={`community-invite-copy-${inv.code}`}
                className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
                aria-label="Copy link"
                title="Copy link"
              >
                <Copy size={12} />
              </button>
              <button
                type="button"
                onClick={() => setDmCode(inv.code)}
                disabled={!inv.is_active}
                data-testid={`community-invite-dm-${inv.code}`}
                className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-40 text-white flex items-center justify-center"
                aria-label="Send via DM"
                title="Send via DM"
              >
                <Send size={11} />
              </button>
              <button
                type="button"
                onClick={() => revoke(inv.code)}
                data-testid={`community-invite-revoke-${inv.code}`}
                className="w-7 h-7 rounded-full bg-transparent border border-rose-500/40 hover:bg-rose-500/10 text-rose-300 flex items-center justify-center"
                aria-label="Revoke"
                title="Revoke"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <CommunityDmInviteModal
        open={!!dmCode}
        onClose={() => setDmCode(null)}
        inviteUrl={dmCode ? buildUrl(dmCode) : ""}
        communityName={communityName}
      />
    </div>
  );
}

// ─── Cam Room (LiveKit) ──────────────────────────────────────────────────
function CamPanel({ communityId }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/communities/${communityId}/private-cam/status`);
        if (!cancelled) setCount(data?.count || 0);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, 10_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [communityId]);

  return (
    <div className="border border-white/10 rounded-md p-6 text-center bg-black/30" data-testid="community-cam-panel">
      <Video size={28} className="mx-auto text-rose-400 mb-3" />
      <h3 className="font-display text-xl mb-1">Private Cam Room</h3>
      {count > 0 ? (
        <div className="mt-1 mb-3 inline-flex items-center gap-1.5 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-3 py-1" data-testid="community-cam-panel-count">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {count} in the room now
        </div>
      ) : (
        <div className="text-xs text-zinc-500 mb-3">Nobody's in the room yet.</div>
      )}
      <p className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed mb-5">
        Every approved member can turn on their own camera — it's a private group video
        call, gated to this community.
      </p>
      {!isNativeIOS() && (
        <Link
          to={`/c/${communityId}/cam`}
          data-testid="community-cam-enter"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-600 to-rose-500 hover:brightness-110 text-white font-semibold rounded-full px-6 py-3 text-sm"
        >
          <Video size={14} /> {count > 0 ? "Jump into the Cam Room" : "Enter the Cam Room"}
        </Link>
      )}
    </div>
  );
}
